// QR Kiosk v2 — KV2-NOTES §7 task NOTE TIMELINE. Renders the general progress-note rows
// (notes[], newest-first — server is source of truth, never re-sorted) plus a section-level
// strip of General-context photos. This is the read-path that makes an added note actually
// APPEAR in the kiosk task detail (the reported bug). SEC-REQ-4: photo bytes resolve ONLY
// through the authenticated /v1/customer/work-orders/files/{id}/download route (via the
// useAuthenticatedBlobUrls hook / Bearer header) — no fileId/name ever in a query string or
// a public URL; authorName/uploadedByUserName render in the BODY only. Tokens only; no MUI/dt/hex.
import React, { useMemo, useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAuthenticatedBlobUrls } from "../../../shared/hooks/useAuthenticatedBlobUrl";
import { KDate } from "../reports/ReportBits";
import { REPORT_DATE_FORMAT, isImageFileName } from "../reports/reportUtils";
import { PhotoCarousel } from "../reports/PhotoCarousel";
import { TaskFileChipGrid } from "./TaskBits";
import { KioskTaskDetail, KioskTaskFile } from "./kioskTasksModels";

const FILE_DOWNLOAD_BASE = "/v1/customer/work-orders/files";

const sectionBorderTop = "1px solid var(--border-soft)";

// One general photo — resolved to a blob: thumbnail (SEC-REQ-4) or a loading placeholder tile.
function PhotoTile({ src, name, onOpen }: { src: string | null; name: string; onOpen?: () => void }) {
  const tileBase: React.CSSProperties = { height: 82, borderRadius: 11, overflow: "hidden", position: "relative" };
  if (!src) {
    return <div style={{ ...tileBase, border: "1px solid var(--border)", background: "color-mix(in oklab, var(--faint) 12%, var(--card))" }} />;
  }
  return (
    <button
      type="button"
      aria-label={name}
      onClick={onOpen}
      style={{
        ...tileBase, border: "1px solid var(--border)", padding: 0, width: "100%", cursor: "zoom-in",
        backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center",
      }}
    />
  );
}

export function TaskNotesTimeline({ tk }: { tk: KioskTaskDetail }) {
  const { translate } = useTranslation("public");
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);

  const notes = tk.notes ?? [];
  const generalFiles = useMemo(() => (tk.files ?? []).filter((f) => f.uploadContext === "General"), [tk.files]);
  const imageFiles = useMemo(() => generalFiles.filter((f) => isImageFileName(f.fileName)), [generalFiles]);
  const docFiles = useMemo(() => generalFiles.filter((f) => !isImageFileName(f.fileName)), [generalFiles]);

  // SEC-REQ-4: authenticated Bearer fetch → blob: URLs. fileId rides the ROUTE PATH of the
  // tenant-scoped download endpoint (the mandated route), never a query string / public URL.
  const base = (window as any).app.config.maintenanceApiBaseUrl;
  const imageSourceUrls = useMemo(
    () => imageFiles.map((f) => `${base}${FILE_DOWNLOAD_BASE}/${f.id}/download`),
    [imageFiles, base]
  );
  const { urls: blobUrls } = useAuthenticatedBlobUrls(imageSourceUrls);

  // Resolved (blob-ready) images, in wire order, for the full-screen carousel.
  const resolved = useMemo(
    () =>
      imageFiles
        .map((f, i) => ({ id: f.id, name: f.fileName, src: blobUrls[i] }))
        .filter((x): x is { id: string; name: string; src: string } => !!x.src),
    [imageFiles, blobUrls]
  );

  // §7.2: section renders iff there is at least one note OR at least one General file.
  if (notes.length === 0 && generalFiles.length === 0) return null;

  const openFile = (file: KioskTaskFile) => {
    const idx = resolved.findIndex((r) => r.id === file.id);
    if (idx >= 0) setViewerIdx(idx);
  };

  return (
    <div
      className="kv2-enter"
      style={{
        margin: "12px 14px 0", background: "var(--card)", border: "1px solid var(--border)",
        borderRadius: 12, boxShadow: "var(--shadow)", overflow: "hidden", flexShrink: 0,
      }}
    >
      <div style={{ padding: "12px 16px 6px" }}>
        <div className="eyebrow" style={{ color: "var(--faint)" }}>{translate("kioskV2TaskNotesTitle")}</div>
      </div>

      {/* Section-level General photo strip (Phase A): all General images, tappable → PhotoCarousel. */}
      {imageFiles.length > 0 && (
        <div style={{ padding: "4px 16px 12px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {imageFiles.map((f, i) => (
              <PhotoTile key={f.id} src={blobUrls[i] ?? null} name={f.fileName} onOpen={() => openFile(f)} />
            ))}
          </div>
        </div>
      )}

      {/* Non-image General files → the shared authenticated download chips. */}
      {docFiles.length > 0 && (
        <div style={{ padding: "0 16px 12px" }}>
          <TaskFileChipGrid files={docFiles} />
        </div>
      )}

      {/* Note rows — newest-first as delivered by the server (do NOT re-sort). */}
      {notes.map((note) => (
        <div
          key={note.id}
          style={{
            padding: "11px 16px 12px", borderTop: sectionBorderTop,
            borderLeft: "3px solid color-mix(in oklab, var(--tier-accent) 55%, var(--border))",
          }}
        >
          {note.text && (
            <div style={{ fontSize: 15, color: "var(--ink-2)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {note.text}
            </div>
          )}
          <div
            style={{
              fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)",
              letterSpacing: "0.02em", marginTop: note.text ? 6 : 0,
            }}
          >
            {note.authorName && note.authorName.length > 0 ? `${note.authorName} · ` : ""}
            <KDate date={note.createdDate} format={REPORT_DATE_FORMAT} />
          </div>
        </div>
      ))}

      {viewerIdx != null && (
        <PhotoCarousel
          images={resolved.map(({ src, name }) => ({ src, name }))}
          initialIndex={viewerIdx}
          onClose={() => setViewerIdx(null)}
        />
      )}
    </div>
  );
}

export default TaskNotesTimeline;
